import { FieldError, TextField, Typography } from '@automata/ui';
import { useFlag } from '@automata/utils/hooks';
import Stack from '@mui/material/Stack';
import startCase from 'lodash/startCase';
import { useMemo } from 'react';

import { TaskProperties } from '../../../../types';
import { validateField } from '../../../../utils';
import { ParameterType } from '../../../GraphEditorToolbars/ParametersToolbar/types';
import { useFieldData } from '../../hooks';
import { ExposingParameterChip } from '../ExposingParameterChip';
import { ExposingParameterPopover } from '../ExposingParameterPopover/ExposingParameterPopover';

interface OneOfFieldsProps {
  collection: string[];
  taskId: string;
  taskProperties: TaskProperties;
  updateTaskParameters: (fieldName: string, value: string) => void;
}

export const OneOfFields = ({
  collection,
  taskId,
  taskProperties,
  updateTaskParameters,
}: OneOfFieldsProps) => {
  const apiBatchingEnabled = useFlag('fe.api_batching');
  const workflowLevelErrorsVisible = useFlag(
    'fe.canvas-builder-workflow-errors-visible'
  );
  const { getFieldData } = useFieldData(taskProperties);

  const oneOfFieldsError = useMemo(() => {
    const numberOfNonEmptyFields = collection.reduce((acc, field) => {
      const { parameter, parameterAppliedValue } = getFieldData(field);

      return !!parameter && !!parameterAppliedValue ? acc + 1 : acc;
    }, 0);

    return numberOfNonEmptyFields > 1
      ? 'Please use only one of the two fields.'
      : null;
  }, [collection, getFieldData]);

  const renderField = (fieldName: string) => {
    const {
      parameter,
      parameterAppliedValue,
      parameterAppliedReferenceId,
      parameterReference,
    } = getFieldData(fieldName);

    if (!parameter) return null;

    const schemaError = validateField(parameterAppliedValue, parameter);
    const error = workflowLevelErrorsVisible
      ? schemaError ?? oneOfFieldsError
      : null;

    return (
      <Stack gap={1} flex={1} key={fieldName}>
        <Stack flexDirection="row" alignItems="flex-end" width="100%">
          <TextField
            disabled={!!parameterAppliedReferenceId}
            error={!!error}
            label={startCase(fieldName)}
            value={parameterAppliedValue}
            onChange={(e) => updateTaskParameters(fieldName, e.target.value)}
          />
          {apiBatchingEnabled && (
            <ExposingParameterPopover
              appliedParameterId={parameterReference?.id}
              parameterName={fieldName}
              taskId={taskId}
              types={ParameterType.STRING}
            />
          )}
        </Stack>
        {parameterAppliedReferenceId && (
          <ExposingParameterChip
            fieldName={fieldName}
            parameter={parameterReference}
            taskId={taskId}
          />
        )}
        <FieldError error={error} />
      </Stack>
    );
  };

  return (
    <Stack gap={1}>
      <Typography variant="subtitle2">
        Use only one of the following two fields.
      </Typography>
      {collection.map(renderField)}
    </Stack>
  );
};
